/***********************/
/* Angular + external */
import { Component, forwardRef, ViewChild, Injector, ChangeDetectionStrategy } from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
import { ObjectUtils } from "@bfx/primeng/utils";

// models
import { ICDTextFieldModel } from "../../data-idc/controls/icd-text-field.model";

export const TEXTFIELD_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ICDTextFieldComponent),
  multi: true,
};

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { ICDBase, ICD_BASE_DEFAULT_HOST_RECORD } from "./icd-base.component";
import { ATDLInterfaceControlModel } from "../../data-atdl/model/atdl-interface-control.model";
import { ICDAuxCheckboxModel } from "../../data-idc/controls/icd-aux-checkbox.model";
import { ICNInputControlState } from "../controls/models/bfx-interface-controls.enums";
import { ICDNResponseCommand } from "../../data-idc/enums/icd-response-cmd.enum";
import { ICDNAuxMode } from "../../data-idc/enums/icd-aux-mode.enum";
import { ATDLNParameterType } from "../../data-atdl/enums/atdl-parameter-type.enum";
import { BfxTextInputComponent } from "../../../../shared/base-components/bfx-text-input";

@Component({
  selector: "icd-text-field",
  host: ICD_BASE_DEFAULT_HOST_RECORD,
  templateUrl: "./html/icd-text-field.html",
  providers: [TEXTFIELD_VALUE_ACCESSOR, ObjectUtils],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ICDTextFieldComponent
  extends ICDBase<ICDTextFieldModel>
  implements ControlValueAccessor
{
  /**********************************************************************/
  /* DATA */
  public config: any = {
    maxLength: 255,
    minLength: 0,
  };

  public subData = {
    text: "", // internal control model
    defaultValue: null,
    initValue: null,
  };

  /************************************/
  onModelChange: Function = () => {};
  onModelTouched: Function = () => {};

  @ViewChild("textInput") textInput: BfxTextInputComponent;

  // public inputDirective: BfxInputDirective;
  // public fieldDirective: BfxFieldTextDirective;

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */

  constructor(public injector: Injector) {
    super(injector);
    this.oeLogger = this.oeLogger.for(__dirname);
    this.initialize();
  }

  /**********************************************************************/
  /* INITIALIZE */

  protected initialize(): void {
    super.initialize();
    this.label = "TextField";
  }

  /**********************************************************************/
  /* DYNAMIC CONTROL API  */

  protected initializeICDSpecificModelProperties(): boolean {
    let error: boolean = false;
    if (this.model instanceof ATDLInterfaceControlModel) {
      if (this.model.validity != null) {
        // maxLength
        if (
          this.model.validity.hasOwnProperty("maxLength") &&
          typeof this.model.validity.maxLength === "number"
        ) {
          this.config.maxLength =
            this.model.validity.maxLength > 0
              ? this.model.validity.maxLength
              : this.config.maxLength;
        }
        // minLength
        if (
          this.model.validity.hasOwnProperty("minLength") &&
          typeof this.model.validity.minLength === "number"
        ) {
          this.config.minLength =
            this.model.validity.minLength >= 0
              ? this.model.validity.minLength
              : this.config.minLength;
        }
      }
      // override min/max for char
      if (this.model.paramType === ATDLNParameterType.CHAR) {
        if (this.config.minLength > 1) {
          this.config.minLength = 1;
        }
        if (this.config.maxLength > 1) {
          this.config.maxLength = 1;
        }
      }
      // INIT VALUE  (Default value)
      if (typeof this.model.initValue === "string") {
        this.data.initValue = this.model.initValue;
        this.data.defaultValue = this.model.initValue;
        this.setAndUpdateValue(this.model.initValue);
      } else if (typeof this.model.defaultValue === "string") {
        this.data.initValue = this.model.defaultValue;
        this.data.defaultValue = this.model.defaultValue;
        this.setAndUpdateValue(this.model.initValue);
      }
    } else {
      error = true;
    }
    return error;
  }

  /**********************************************************************/
  /* DYNAMIC CONTROL - HANDLERS */

  protected handleConstraintUpdate(data: any): void {
    this.handleDynResponses(this.processExternalStateFlowDependency(data, new Map()));
  }

  /**********************************************************************/
  /* ControlValueAccessor */

  writeValue(value: any): void {
    this.data.value = value;
    this.model.value = this.data.value;
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  /**********************************************************************/
  /* EVENTS */

  public onFocus(event: any): void {
    this.inputFocused = true;
    this.dirty = true;
    this.data.state = ICNInputControlState.ACTIVE;
    this.data.errorMsg = "";
  }

  public onBlur(event: any): void {
    let dynResponses = new Map();
    this.inputFocused = false;
    this.updateValue();
    this.validate();

    dynResponses.set(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
    dynResponses = this.processAuxStateFlow(dynResponses);
    this.handleDynResponses(dynResponses);
  }

  public onChange(event: any): void {
    this.onModelChange(this.data.value);
  }

  public focusInput() {
    setTimeout(() => this.textInput?.focus(), 50);
  }

  /**********************************************************************/
  /* EXTERNAL API */

  public setDefault(): void {
    this.cd.markForCheck();
    this.setAndUpdateValue(this.data.defaultValue);
  }

  protected setAndUpdateValue(value: any): void {
    this.cd.markForCheck();
    if (value == null) {
      this.set(null);
      this.updateValue();
    } else if (typeof value === "string" || value instanceof ICDTextFieldModel) {
      this.set(value);
      this.updateValue();
    } else {
      logger.out.error(`ERROR: ${this.constructor.name}.setAndUpdateValue(): Invalid value`);
    }
  }

  public updateValue(): void {
    this.cd.markForCheck();
    this.data.defaultValue =
      typeof this.data.text === "string" ? this.data.text : this.data.defaultValue;
    this.data.value = this.compose();
    this.writeValue(this.data.value);
  }

  public load(value: ICDTextFieldModel, propagate?: boolean): void {
    let dynResponses = new Map();

    if (value instanceof ICDTextFieldModel || value === null) {
      this.cd.markForCheck();
      this.set(value);
      this.updateValue();
      this.validate();

      dynResponses.set(ICDNResponseCommand.LOAD_COMPLETE, null);

      dynResponses = this.processAuxStateFlow(dynResponses);

      if (typeof propagate === "boolean" && propagate) {
        dynResponses.set(ICDNResponseCommand.UPDATE_VALUE, this.data.value);
      }

      this.handleDynResponses(dynResponses);
    }
  }

  /**
   * The set() method is used in order to assign value to the internal(displayed) control model ('this.data.text') as well as
   * the auxCheckbox when proper type (ICDTextFieldModel) is provided.
   */
  public set(value: ICDTextFieldModel | string): void {
    this.cd.markForCheck();
    if (value instanceof ICDTextFieldModel || typeof value === "string" || value == null) {
      this.dirty = true;
      if (value instanceof ICDTextFieldModel && value.auxModel instanceof ICDAuxCheckboxModel) {
        this.data.value = value;
        this.disabled = this.data.disabled = value.disabled;
        if (value.auxModel.enabled) {
          this.auxCheckbox.enabled = value.auxModel.enabled;
          this.auxCheckbox.mode = value.auxModel.mode;
          if (value.auxModel.mode === ICDNAuxMode.DISABLED) {
            // value assignment
            this.data.text = "";
            // checkbox assignment
            this.auxCheckbox.value = false;
            this.auxCheckbox.locked = true;
          } else if (value.auxModel.mode === ICDNAuxMode.REQUIRED) {
            // value assignment
            this.data.text = value.value;
            // checkbox assignment
            this.auxCheckbox.value = true;
            this.auxCheckbox.locked = true;
          } else if (value.auxModel.mode === ICDNAuxMode.OPTIONAL) {
            this.data.text = value.value;
            //
            this.auxCheckbox.value = value.auxModel.value;
            this.auxCheckbox.locked = false;
          } else {
            logger.out.error(`ERROR: ${this.constructor.name}.set(): Invalid auxMode`);
          }
        } else {
          this.data.text = value.value;
          //
          this.auxCheckbox.enabled = false;
          this.auxCheckbox.mode = ICDNAuxMode.DISABLED;
          this.auxCheckbox.value = false;
          this.auxCheckbox.locked = true;
        }
      } else if (value instanceof ICDTextFieldModel && value.auxModel === null) {
        this.data.text = value.value;
      } else if (typeof value === "string") {
        this.data.text = value;
      } else if (value == null) {
        this.data.text = "";
      } else {
        logger.out.error(`ERROR: ${this.constructor.name}.set(): Invalid value type`);
      }
    }
  }

  /**
   * The compose() method is used in order to generate an outgoing model for the control (in this case ICDTextFieldModel)
   * which will be assigned to the main model 'this.data.value' and propagated to the panel.
   */
  private compose(): ICDTextFieldModel {
    let model = new ICDTextFieldModel();
    let auxModel = new ICDAuxCheckboxModel();
    if (this.auxCheckbox.enabled) {
      auxModel.auxId = this.auxCheckbox.id;
      auxModel.enabled = this.auxCheckbox.enabled;
      auxModel.mode = this.auxCheckbox.mode;
      auxModel.value = this.auxCheckbox.value;
      if (this.auxCheckbox.mode === ICDNAuxMode.DISABLED) {
        model.value = null;
      } else if (this.auxCheckbox.mode === ICDNAuxMode.REQUIRED) {
        model.value =
          typeof this.data.text === "string" && this.data.text.length > 0 ? this.data.text : null;
      } else if (this.auxCheckbox.mode === ICDNAuxMode.OPTIONAL) {
        model.value = this.auxCheckbox.value ? this.data.text : null;
      } else {
        logger.out.error(`ERROR: ${this.constructor.name}.set(): Invalid auxMode`);
        model.value =
          typeof this.data.text === "string" && this.data.text.length > 0 ? this.data.text : null;
      }
    } else {
      auxModel.auxId = this.auxCheckbox.id;
      auxModel.enabled = this.auxCheckbox.enabled;
      auxModel.mode = this.auxCheckbox.mode;
      //
      model.value =
        typeof this.data.text === "string" && this.data.text.length > 0 ? this.data.text : null;
    }
    model.nulledByDependency = this.data?.value?.nulledByDependency ?? false;
    model.disabled = this.disabled || this.data.disabled || !this.visible;
    model.auxModel = auxModel;
    return model;
  }

  public validateIC(): boolean {
    this.cd.markForCheck();
    // checkbox is active, but not checked
    if (this.auxCheckbox.enabled && !this.auxCheckbox.value) {
      this.data.state = ICNInputControlState.INACTIVE;
      this.data.errorMsg = "";
      return true;
    }
    // standard validation
    if (this.data.text != null && typeof this.data.text === "string") {
      if (this.data.text.length > this.config.maxLength) {
        this.data.state = ICNInputControlState.INVALID;
        this.data.errorMsg = `${this.label} must be less than ${
          this.config.maxLength + 1
        } characters long.`;
        return false;
      } else if (this.data.text.length < this.config.minLength) {
        this.data.state = ICNInputControlState.INVALID;
        this.data.errorMsg =
          this.data.text.length === 0
            ? `${this.label} must not be empty`
            : `${this.label} must at least ${this.config.minLength} characters long.`;
        return false;
      } else {
        this.data.state = ICNInputControlState.VALID;
        this.data.errorMsg = "";
        return true;
      }
    } else {
      if (this.dirty) {
        if (
          this.required ||
          !(this.locked || (this.disabled && this.auxCheckbox.enabled == false))
        ) {
          this.data.state = ICNInputControlState.INVALID;
          this.data.errorMsg = `${this.label} must not be empty`;
          return false;
        } else {
          this.data.state = ICNInputControlState.VALID;
          this.data.errorMsg = "";
          return true;
        }
      } else {
        this.data.state = ICNInputControlState.INACTIVE;
        this.data.errorMsg = "";
        return true;
      }
    }
  }

  public resetIC(): void {
    this.cd.markForCheck();
    this.data.text = "";
    this.data.defaultValue = null;
    this.data.initValue = null;
    // Label
    this.label = "TextField";
    // Config
    this.config.maxLength = 255;
    this.config.minLength = 0;
  }
}
